<script setup lang="ts">
import { useCmsHead } from "@/composables/useCmsHead";
import type { Ref } from "vue";
import { useCategorySearch } from "#imports";
import type { Schemas } from "#shopware";
import {useCachedCategory} from "~/components/server/useCachedCategory";

const props = defineProps<{
  navigationId: string;
}>();

const { search } = useCategorySearch();
const route = useRoute();
const { clearBreadcrumbs, buildDynamicBreadcrumbs } = useBreadcrumbs();
const { apiClient } = useShopwareContext();

const categoryResponse = ref();
categoryResponse.value = await useCachedCategory(props.navigationId, route.query);

const breadCrumbResponse = ref();
breadCrumbResponse.value = await apiClient
    .invoke("readBreadcrumb get /breadcrumb/{id}", {
      pathParams: {
        id: props.navigationId,
      },
    })
    .catch(() => {
      console.error("Error while fetching breadcrumbs");
    });

clearBreadcrumbs();
const breadcrumbData = breadCrumbResponse.value?.data;
if (Array.isArray(breadcrumbData) && breadcrumbData.length > 0) {
  const crumbs = breadcrumbData.map((bc: any) => ({
    name: bc.translated?.name || bc.name,
    path: bc.type !== "folder" && bc.seoUrls?.[0]?.seoPathInfo ? `/${bc.seoUrls[0].seoPathInfo}` : undefined,
  }));
  useBreadcrumbs(crumbs);
} else if (breadcrumbData?.breadcrumbs) {
  buildDynamicBreadcrumbs(breadcrumbData);
}

if (!categoryResponse.value) {
  console.error("[FrontendNavigationPage.vue]", 'Category could not be found or loaded.');
  throw createError({
    statusCode: 404,
    statusMessage: `Category could not be found or loaded.`,
  });
}

const { category } = useCategory(categoryResponse as Ref<Schemas["Category"]>);

const runtimeConfig = useRuntimeConfig();
const { locale } = useI18n();

useCmsHead(category, {
  mainShopTitle: runtimeConfig.public.general.shopTitle[locale.value],
});
</script>

<template>
  <div>
    <CmsPage v-if="category?.cmsPage" :content="category.cmsPage" />
  </div>
</template>